import { FC, ReactElement, useState, useEffect, useMemo } from 'react';

import { Icon } from '@pp/core/src/components/icons/icon';
import { Icons } from '@pp/core/src/components/icons/iconNames';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { numberFormatter } from '../../../config/utils';
import { useHighflyerGameStateSlice } from '../../../store/slices/highflyerGame/selectors';
import { useRecentResultDetailStateSlice } from '../../../store/slices/recentResultDetail/selectors';

import styles from './SingleResult.module.css';

export interface SpinResultProp {
  active?: boolean;
  type: number;
  value: number;
}

enum CssType {
  btnValue1 = 2,
  btnValue2 = 5,
  btnValue3 = 10,
  btnValue4 = 100,
  btnValue5 = Infinity,
}

export const SingleResult: FC<SpinResultProp> = ({ type, active, value }): ReactElement => {
  const [cssType, setCssType] = useState('');
  const locale = useSettingsSlice.currentLanguage();

  const formatedNumber = useMemo(() => {
    return numberFormatter(value, locale);
  }, [locale, value]);

  const formattedMultiplier = formatedNumber;
  const betsOpenStatus = useGameStateSlice.getCurrentStatus();
  const isGameCancelled = betsOpenStatus === GameStatus.GameCancelled;
  const timeRemaining = useRecentResultDetailStateSlice.getTimeRemaining();
  const showInPlayTimerButton = useHighflyerGameStateSlice.timerInPlayBtnStatus();

  useEffect(() => {
    if (value < 0) {
      setCssType(styles.btnValue0);
    } else {
      for (const key in CssType) {
        if (value <= Number(CssType[key])) {
          setCssType(styles[key as keyof typeof CssType]);
          return;
        }
      }
    }
  }, [value]);

  const footerUpdate = useModifiers(styles, 'update', {
    button: !showInPlayTimerButton && active,
    buttonDone: showInPlayTimerButton && active,
  });

  return (
    <div className={`${styles.singleResult} ${cssType} ${isGameCancelled && styles.cancelledResults} ${footerUpdate}`}>
      {isGameCancelled && value === -1 && <Icon className={styles.cancelledIcon} icon={Icons.CancelledResults}></Icon>}
      {!isGameCancelled && value === -1 && timeRemaining <= 0 && (
        <span>
          <L10nText tk="IN_PLAY_CAPS" />
        </span>
      )}
      {!isGameCancelled && value === -1 && timeRemaining > 0 && (
        <span>
          {timeRemaining}
          <L10nText tk="NXT_GAME_10" />
        </span>
      )}
      {value !== -1 && <span>{formattedMultiplier}x</span>}
    </div>
  );
};